import React, { useState, useEffect } from 'react';
import { useLazyCommand, useEvent } from "@soap/modules";
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { secondsToMMSS, mmssToSeconds } from '../../utils/timeFormat';
import type { Assessment } from './types';
import { timingModeKeys } from './types';
import { ASSESSMENT_TIMING_DEFAULTS } from '../../../z50-domaincomp/constants/assessment-timing-defaults';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';

const DEFAULT_SECONDS_PER_QUESTION = ASSESSMENT_TIMING_DEFAULTS.DEFAULT_SECONDS_PER_QUESTION;

export function TimingModeSelector({ 
  assessment, 
  onTimingModeChange 
}: { 
  assessment: Assessment; 
  onTimingModeChange: (mode: string) => void;
}) {
  const [updateTimingMode] = useLazyCommand("C197v1_UpdateAssessmentTimingMode");
  const [updateTimings] = useLazyCommand("C195v1_UpdateAssessmentTimeLimits");
  const [overallTimeLimit, setOverallTimeLimit] = useState(assessment.OverallTimeLimit);
  const [overallTimeLimitDisplay, setOverallTimeLimitDisplay] = useState(secondsToMMSS(assessment.OverallTimeLimit));
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [versionBeforeChange, setVersionBeforeChange] = useState<number | null>(null);

  useEffect(() => {
    setOverallTimeLimit(assessment.OverallTimeLimit);
    setOverallTimeLimitDisplay(secondsToMMSS(assessment.OverallTimeLimit));
  }, [assessment.OverallTimeLimit]);

  // Reset processing state if dialog is closed externally
  useEffect(() => {
    if (!showConfirmDialog && isProcessing) {
      setIsProcessing(false);
      setPendingMode(null);
    }
  }, [showConfirmDialog, isProcessing]);

  const getModeLabel = (mode: string) => {
    switch (mode) {
      case timingModeKeys.NotTimed: return 'Not Timed';
      case timingModeKeys.PerAssessment: return 'Overall Assessment';
      case timingModeKeys.PerSection: return 'Per Section';
      case timingModeKeys.PerQuestion: return 'Per Question';
      default: return mode;
    }
  };

  const isTimedMode = (mode: string) => {
    return mode === timingModeKeys.PerAssessment || 
           mode === timingModeKeys.PerSection || 
           mode === timingModeKeys.PerQuestion;
  };

  const handleTimingModeChange = (newMode: string) => {
    const currentMode = assessment.TimingMode;
    
    // Show confirmation if switching away from a timed mode
    if (isTimedMode(currentMode) && newMode !== currentMode) {
      setPendingMode(newMode);
      setShowConfirmDialog(true);
    } else {
      // No confirmation needed, proceed with change
      proceedWithModeChange(newMode);
    }
  };

  // Listen for timing update event to know when timing mode change is complete
  useEvent({
    eventName: "E268v1_AssessmentTimingUpdated",
    onEventReceived: (event: any) => {
      const eventAssessmentId = event.e268_AssessmentId || event.E268_AssessmentId;
      
      if (eventAssessmentId === assessment.AssessmentId && isProcessing) {
        // Update overall time limit from event data
        if (event.e268_OverallTimeLimitInSeconds !== undefined) {
          setOverallTimeLimit(event.e268_OverallTimeLimitInSeconds);
          setOverallTimeLimitDisplay(secondsToMMSS(event.e268_OverallTimeLimitInSeconds));
        }
        
        // Change is complete!
        setIsProcessing(false);
        setShowConfirmDialog(false);
        setPendingMode(null);
      }
    }
  }, [assessment.AssessmentId, isProcessing]);

  const proceedWithModeChange = (newMode: string) => {
    setIsProcessing(true);
    
    updateTimingMode({
      C197_AssessmentId: assessment.AssessmentId,
      C197_AssessmentTimingModeKey: newMode,
    });
    
    onTimingModeChange(newMode);
    // Don't close dialog yet - wait for E149 event
  };

  const handleConfirm = () => {
    if (pendingMode) {
      proceedWithModeChange(pendingMode);
    }
  };

  const handleCancel = () => {
    if (!isProcessing) {
      setShowConfirmDialog(false);
      setPendingMode(null);
    }
  };

  const handleOverallTimeLimitChange = (value: string) => {
    setOverallTimeLimitDisplay(value);
    const timeInSeconds = mmssToSeconds(value);
    if (timeInSeconds !== null && timeInSeconds >= 10 && timeInSeconds <= 3600) {
      setOverallTimeLimit(timeInSeconds);
      updateTimings({
        $type: "C195v1_UpdateAssessmentTimeLimits",
        C195_AssessmentId: assessment.AssessmentId,
        C195_AssessmentTimingModeKey: timingModeKeys.PerAssessment,
        C195_TimeInSeconds: timeInSeconds,
      });
    }
  };

  return (
    <>
      <Card className="p-4 mb-4 bg-action-blue/10">
        <Label className="text-sm font-medium mb-3 block">
          Select How The Assessment Should Be Timed <span className="text-gray-600 italic">(MM:SS)</span>
        </Label>
        <div className="flex flex-wrap gap-4 mb-4">
          <label className={`flex items-center space-x-2 ${isProcessing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="timing_mode"
              value={timingModeKeys.NotTimed}
              checked={assessment.TimingMode === timingModeKeys.NotTimed}
              onChange={(e) => handleTimingModeChange(e.target.value)}
              disabled={isProcessing}
              className="w-4 h-4 text-action-blue"
            />
            <span className="text-sm">Not Timed</span>
          </label>
          <label className={`flex items-center space-x-2 ${isProcessing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="timing_mode"
              value={timingModeKeys.PerAssessment}
              checked={assessment.TimingMode === timingModeKeys.PerAssessment}
              onChange={(e) => handleTimingModeChange(e.target.value)}
              disabled={isProcessing}
              className="w-4 h-4 text-action-blue"
            />
            <span className="text-sm">Overall Assessment</span>
          </label>
          <label className={`flex items-center space-x-2 ${isProcessing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="timing_mode"
              value={timingModeKeys.PerSection}
              checked={assessment.TimingMode === timingModeKeys.PerSection}
              onChange={(e) => handleTimingModeChange(e.target.value)}
              disabled={isProcessing}
              className="w-4 h-4 text-action-blue"
            />
            <span className="text-sm">Per Section</span>
          </label>
          <label className={`flex items-center space-x-2 ${isProcessing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="timing_mode"
              value={timingModeKeys.PerQuestion}
              checked={assessment.TimingMode === timingModeKeys.PerQuestion}
              onChange={(e) => handleTimingModeChange(e.target.value)}
              disabled={isProcessing}
              className="w-4 h-4 text-action-blue"
            />
            <span className="text-sm">Per Question</span>
          </label>
        </div>
        {assessment.TimingMode === timingModeKeys.PerAssessment && (
          <div className="flex items-center gap-3">
            <Label htmlFor="overall-time-limit" className="text-sm">Overall Time Limit (MM:SS)</Label>
            <Input
              id="overall-time-limit"
              type="text"
              placeholder="00:00"
              value={overallTimeLimitDisplay}
              onChange={(e) => handleOverallTimeLimitChange(e.target.value)}
              onBlur={() => {
                const seconds = mmssToSeconds(overallTimeLimitDisplay);
                if (seconds === null || seconds < 10 || seconds > 3600) {
                  setOverallTimeLimitDisplay(secondsToMMSS(overallTimeLimit));
                } else {
                  setOverallTimeLimitDisplay(secondsToMMSS(seconds));
                }
              }}
              className="w-24"
              pattern="[0-9]{1,2}:[0-5][0-9]"
            />
          </div>
        )}
      </Card>

      <Dialog open={showConfirmDialog} onOpenChange={(open) => {
        // Prevent closing while processing
        if (!isProcessing) {
          setShowConfirmDialog(open);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Timing Mode Change</DialogTitle>
            <DialogDescription>
              Switching from <strong>{getModeLabel(assessment.TimingMode)}</strong> to <strong>{pendingMode ? getModeLabel(pendingMode) : ''}</strong> will:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
              <li>Clear all existing time settings for the previous mode</li>
              <li>Set default time values based on 30 seconds per question for the new mode</li>
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              Any custom time settings you have configured will be lost.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isProcessing}>
              {isProcessing ? 'Processing...' : 'Confirm Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

